import { useEffect, useState } from 'react';
import Proptypes from 'prop-types';
import _get from 'lodash/get';
import _orderBy from 'lodash/orderBy';

import styles from './chart-crosshair-tooltips.module.scss';
import { augmentClassName } from '../../../utils/GlobalCssProxy';
import format from '../../../utils/format';
import chartLineColorsByColorCode from '../../../helpers/statics/chart-color';

const className = augmentClassName(styles);

const formatTooltipData = indicators => {
  const fundsTooltipData = [];

  indicators.forEach((fund, index) => {
    const fundData = _get(fund, 'data');
    const coordinateData = _get(fund, 'coords');
    const positionTop = _get(coordinateData, 'y.value', 0) - 5;
    const positionLeft = _get(coordinateData, 'x', 0) - 4;

    fundsTooltipData.push({
      fundName: fundData.fundName,
      tooltipColor: chartLineColorsByColorCode[fundData.chartColor],
      positionLeft,
      positionTop,
      value: fundData.value,
      date: new Date(fundData.date)
    });
  });

  return _orderBy(fundsTooltipData, ['value'], ['desc']);
};

const renderTooltips = (tooltipPosition, chartType, fundsData = []) => {
  const checkFundsDataForIndex = (index, fundsDataLength) => {
    if (index < fundsDataLength - 1) {
      return 'border-bottom-dashed-hypo';
    }

    return '';
  };

  const renderByChartType = () => {
    switch (chartType) {
      case 'hypoChart':
        return (
          <>
            <div
              {...className('chart-tooltip')}
              id={'chart-tooltip'}
              style={
                tooltipPosition.margin >= 10
                  ? {
                      position: 'absolute',
                      top: `-${tooltipPosition.offset}px`,
                      left: `${tooltipPosition.margin}px`
                    }
                  : {
                      position: 'absolute',
                      top: `-${tooltipPosition.offset}px`,
                      right: `10px`
                    }
              }
            >
              {fundsData.map((fund, index) => {
                return (
                  <div
                    data-key={`${fund.fundName || ''}-${index}`}
                    key={`${fund.fundName || ''}-${index}`}
                    {...className(
                      'chart-tooltip-fund-data',
                      'd-flex',
                      'p-3',
                      'align-items-center',
                      checkFundsDataForIndex(index, fundsData.length)
                    )}
                  >
                    <div {...className('d-flex')}>
                      <div {...className('label-dot', 'mr-2', `bg-${fund.tooltipColor}`)} />
                      <div {...className('fund-names', 'text-muted')}>{fund.fundName}</div>
                    </div>
                    <div
                      {...className(
                        'w-100',
                        'font-weight-bold',
                        'mt-2',
                        'ml-4',
                        `color-${fund.tooltipColor}`
                      )}
                    >
                      {format.price(fund.value, { formatString: '$0,0' }, true)}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        );

      case 'priceChart':
        return (
          <>
            {' '}
            <div
              {...className('price-chart-tooltip')}
              id={'chart-tooltip'}
              style={
                tooltipPosition.margin >= 10
                  ? {
                      position: 'absolute',
                      top: `-${tooltipPosition.offset}px`,
                      left: `${tooltipPosition.margin}px`
                    }
                  : {
                      position: 'absolute',
                      top: `-${tooltipPosition.offset}px`,
                      right: `10px`
                    }
              }
            >
              {fundsData.map((fund, index) => {
                return (
                  <div
                    data-key={`${fund.fundName || ''}-${index}`}
                    key={`${fund.fundName || ''}-${index}`}
                  >
                    <div {...className('d-flex')}>
                      <div {...className('fund-names', 'text-muted')}>{fund.fundName}</div>
                    </div>
                    <div {...className('w-100', 'font-weight-bold', `color-${'blue'}`)}>
                      <span {...className('chart-info-text-space')}>{format.date(fund.date)}</span>

                      <span>Close: </span>
                      {format.price(fund.value, { formatString: '0.00' }, true)}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        );
      default:
        return '';
    }
  };

  return <>{renderByChartType()}</>;
};

const renderChartMarkers = (tooltipPosition, chartType, fundsData = []) => {
  return fundsData.map((fund, index) => {
    return (
      <div
        data-key={`${fund.fundName || ''}-${index}`}
        key={`${fund.fundName || ''}-${index}`}
        {...className('rounded-hollow', `border-${fund.tooltipColor}`)}
        style={{
          position: 'absolute',
          left: `${fund.positionLeft}px`,
          top: `${fund.positionTop}px`
        }}
      >
        {/*Attaches tooltip to top chart marker*/}
        {index === 0 && renderTooltips(tooltipPosition, chartType, fundsData)}
      </div>
    );
  });
};

const CrosshairTooltips = ({ chart, chartType }) => {
  const [fundsTooltipData, setFundsTooltipData] = useState([]);
  const [tooltipPosition, setTooltipPosition] = useState(0);
  const resetState = () => {
    setFundsTooltipData([]);
  };

  const getTooltipPosition = crosshairData => {
    const tooltipElement = document.getElementById('chart-tooltip');
    if (!tooltipElement) return;

    const coordinates = _get(crosshairData, 'coords');
    const rect = chart.rootMouse.node().getBoundingClientRect();
    let margin = 10;
    const width = tooltipElement.offsetWidth;
    const left = rect.left + margin + coordinates.x;
    const tooltipHeight = tooltipElement.offsetHeight;
    const tooltipOffset = tooltipHeight / 2;

    // Bump left if right collision
    if (left + width > rect.left + chart.size.width - chart.panels[0].size.padding.right) {
      margin = coordinates.x - left - chart.panels[0].size.padding.right;
      setTooltipPosition({ margin: `${margin}`, offset: tooltipOffset });
    } else {
      setTooltipPosition({ margin: `${margin}`, offset: tooltipOffset });
    }
  };

  const handleCrosshairMove = crosshairData => {
    if (crosshairData) {
      const { panels } = crosshairData;
      const indicators = _get(panels, '[0].indicators');
      setFundsTooltipData(formatTooltipData(indicators));
      getTooltipPosition(crosshairData);
    }
  };

  useEffect(() => {
    if (!chart || !chart.eventEmitter) return;
    chart.eventEmitter.on('CROSSHAIR_MOVE', handleCrosshairMove);
    chart.eventEmitter.on('RESIZE', resetState);
    chart.eventEmitter.on('MOUSE_LEAVE', resetState);

    // eslint-disable-next-line consistent-return
    return () => [
      chart.eventEmitter.removeListener('CROSSHAIR_MOVE', handleCrosshairMove),
      chart.eventEmitter.removeListener('RESIZE', resetState),
      chart.eventEmitter.removeListener('MOUSE_LEAVE', resetState)
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  return fundsTooltipData.length > 0 ? (
    <>{renderChartMarkers(tooltipPosition, chartType, fundsTooltipData)}</>
  ) : (
    <></>
  );
};

CrosshairTooltips.propTypes = {
  chart: Proptypes.object,
  chartType: Proptypes.string
};

export default CrosshairTooltips;
